import * as Y from "yjs";
import { entitySchemas, type EntityByType, type EntityType } from "../schemas";
import {
  changesetField,
  changesetsMapName,
  entityMap,
  jsonEqual,
  parseEntity,
  readEntity,
  type ChangeOrigin,
  type EntityMap,
  type Snapshot,
} from "./doc";

export class CommandError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CommandError";
  }
}

/** Read and write access to the document inside one changeset. */
export interface DocWriter {
  readonly origin: ChangeOrigin;
  get<T extends EntityType>(type: T, id: string): EntityByType[T] | null;
  /** All non-deleted entities of a type. */
  list<T extends EntityType>(type: T): EntityByType[T][];
  /**
   * Creates the entity (all required fields must be given) or changes only the given fields.
   * `undefined` removes an optional field. Throws CommandError if the result is invalid.
   */
  put<T extends EntityType>(type: T, id: string, fields: Partial<EntityByType[T]>): EntityByType[T];
  /** Tombstones the entity. */
  remove(type: EntityType, id: string): void;
}

/**
 * Runs `fn` in one Yjs transaction tagged with the changeset. Commands must write through the
 * writer only, so every field change carries the changeset ID for the server's op log.
 */
function parsedType(doc: Y.Doc, type: EntityType): Map<string, unknown> {
  const map = new Map<string, unknown>();
  entityMap(doc, type).forEach((value, id) => {
    const e = value instanceof Y.Map ? parseEntity(type, value.toJSON()) : null;
    if (e) {
      map.set(id, e);
    }
  });
  return map;
}

export function runChange<R>(doc: Y.Doc, origin: ChangeOrigin, fn: (w: DocWriter) => R): R {
  // Write-through cache of parsed entities, filled per type on the first list() of that type,
  // so listing after a write doesn't re-parse the whole document.
  const cache = new Map<EntityType, Map<string, unknown>>();
  let wrote = false;
  let result!: R;
  const cached = (type: EntityType): Map<string, unknown> => {
    let map = cache.get(type);
    if (!map) {
      map = parsedType(doc, type);
      cache.set(type, map);
    }
    return map;
  };
  doc.transact(() => {
    const writer: DocWriter = {
      origin,
      get: (type, id) => readEntity(doc, type, id),
      list: <T extends EntityType>(type: T) => {
        const out: EntityByType[T][] = [];
        for (const e of cached(type).values()) {
          if (!(e as { deleted: boolean }).deleted) {
            out.push(e as EntityByType[T]);
          }
        }
        return out;
      },
      put: (type, id, fields) => {
        const { entity, changed } = putEntity(doc, origin, type, id, fields);
        cache.get(type)?.set(id, entity);
        if (changed) {
          wrote = true;
        }
        return entity;
      },
      remove: (type, id) => {
        const existing = entityMap(doc, type).get(id);
        if (existing instanceof Y.Map && existing.get("deleted") !== true) {
          wrote = true;
          existing.set("deleted", true);
          existing.set(changesetField, origin.changesetId);
          const map = cache.get(type);
          if (map) {
            const e = readEntity(doc, type, id);
            if (e) {
              map.set(id, e);
            } else {
              map.delete(id);
            }
          }
        }
      },
    };
    result = fn(writer);
    if (wrote) {
      const { changesetId, ...meta } = origin;
      doc.getMap(changesetsMapName).set(changesetId, meta);
    }
  }, origin);
  return result;
}

function putEntity<T extends EntityType>(
  doc: Y.Doc,
  origin: ChangeOrigin,
  type: T,
  id: string,
  fields: Partial<EntityByType[T]>,
): { entity: EntityByType[T]; changed: boolean } {
  const top = entityMap(doc, type);
  const existing = top.get(id);
  const current = existing instanceof Y.Map ? (existing.toJSON() as Record<string, unknown>) : {};
  delete current[changesetField];
  const value = validated(type, id, { ...current, ...fields, id });
  let target: EntityMap;
  if (existing instanceof Y.Map) {
    target = existing;
  } else {
    target = new Y.Map<unknown>();
    top.set(id, target);
  }
  let changed = !(existing instanceof Y.Map);
  for (const [k, v] of Object.entries(value)) {
    if (!jsonEqual(target.get(k), v)) {
      target.set(k, v);
      changed = true;
    }
  }
  for (const k of Array.from(target.keys())) {
    if (k !== changesetField && !(k in value)) {
      target.delete(k);
      changed = true;
    }
  }
  if (changed) {
    target.set(changesetField, origin.changesetId);
  }
  return { entity: parseEntity(type, value) as EntityByType[T], changed };
}

/**
 * Runs `fn` against an in-memory overlay of the document, with the same validation as
 * `runChange`, and leaves the document untouched: a cheap all-or-nothing check before running
 * a command for real (Yjs transactions don't roll back). `base`, when given, must be a current
 * snapshot of `doc` (e.g. a SnapshotCache's) and saves parsing whole types for `list()`.
 */
export function dryRunChange<R>(
  doc: Y.Doc,
  origin: ChangeOrigin,
  fn: (w: DocWriter) => R,
  base?: Snapshot,
): R {
  const overlay = new Map<EntityType, Map<string, Record<string, unknown>>>();
  const written = (type: EntityType) => {
    let map = overlay.get(type);
    if (!map) {
      map = new Map();
      overlay.set(type, map);
    }
    return map;
  };
  const raw = (type: EntityType, id: string): Record<string, unknown> | undefined => {
    const own = overlay.get(type)?.get(id);
    if (own) {
      return own;
    }
    const m = entityMap(doc, type).get(id);
    if (!(m instanceof Y.Map)) {
      return undefined;
    }
    const json = m.toJSON() as Record<string, unknown>;
    delete json[changesetField];
    return json;
  };
  const writer: DocWriter = {
    origin,
    get: (type, id) => {
      const r = raw(type, id);
      return r ? parseEntity(type, r) : null;
    },
    list: <T extends EntityType>(type: T) => {
      const own = overlay.get(type);
      const source: ReadonlyMap<string, unknown> = base ? base[type] : parsedType(doc, type);
      const out: EntityByType[T][] = [];
      const add = (e: unknown) => {
        if (e && !(e as { deleted: boolean }).deleted) {
          out.push(e as EntityByType[T]);
        }
      };
      for (const [id, e] of source) {
        const changed = own?.get(id);
        add(changed ? parseEntity(type, changed) : e);
      }
      for (const [id, e] of own ?? []) {
        if (!source.has(id)) {
          add(parseEntity(type, e));
        }
      }
      return out;
    },
    put: (type, id, fields) => {
      const value = validated(type, id, { ...(raw(type, id) ?? {}), ...fields, id });
      written(type).set(id, value);
      return parseEntity(type, value) as EntityByType[typeof type];
    },
    remove: (type, id) => {
      const r = raw(type, id);
      if (r && r.deleted !== true) {
        written(type).set(id, { ...r, deleted: true });
      }
    },
  };
  return fn(writer);
}

function validated(type: EntityType, id: string, merged: Record<string, unknown>) {
  for (const [k, v] of Object.entries(merged)) {
    if (v === undefined) {
      delete merged[k];
    }
  }
  const parsed = entitySchemas[type].safeParse(merged);
  if (!parsed.success) {
    throw new CommandError(
      `Invalid ${type} ${id}: ${parsed.error.issues.map((i) => `${i.path.join(".")} ${i.message}`).join("; ")}`,
    );
  }
  return parsed.data as Record<string, unknown>;
}
